import { StageDef } from '../automationMachineInterfaces'
import { REDDIT_AUTOMATION_STAGE_STATUSES } from './redditAutomationStageStatuses'
import * as redditUrls from './redditUrls'
import { getSwitchValue, toggleSwitch } from './switchUtils'
import { RedditSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

type PersonalizedAdsSetting = {
  key: RedditSettingKey
  label: string
  oldLabel: string
  section: string
}

const settings: PersonalizedAdsSetting[] = [
  {
    key: RedditSettingKey.PERSONALIZE_ADS,
    oldLabel:
      'Personalize ads on Reddit based on information and activity from our partners.',
    label:
      'Personalize ads on Reddit based on information and activity from our partners',
    section: 'settings-privacy-section',
  },
]

const getCurrentValueFn = (key: string) =>
  getCurrentValueFnFromKey(key, (value) => (value === true ? 'On' : 'Off'))

const getIsFulfilledFn = (key: string) =>
  getIsFulfilledFnFromKey(key, (value) => value === false)

const gatherStageDefs: StageDef[] = settings.map((s) => ({
  type: enums.StageType.ACTION,
  key: `GATHER_REDDIT_SETTING_${s.key}`,
  status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
  url: redditUrls.privacySettingsUrl,
  action: async (_machineContext, userContext) => {
    const value = await getSwitchValue(s.section, s.label, s.oldLabel)
    await userContext.updateStoredInitialValues(
      {
        [s.key]: value,
      },
      enums.RedditRecommendationKey.REDDIT_PERSONALIZED_ADS,
    )
  },
}))

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UPDATE_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: redditUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      if (
        !userContext.settingsSelected ||
        !userContext.settingsSelected[s.key]
      ) {
        return null
      }
      const currentValue = await getSwitchValue(s.section, s.label, s.oldLabel)
      if (!userContext.isDry && currentValue !== false) {
        await toggleSwitch(s.section, s.label, s.oldLabel)
      }
      return null
    },
  })),
]

const undoStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UNDO_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: redditUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      if (!userContext.initialValues || !userContext.initialValues[s.key]) {
        return null
      }
      const currentValue = await getSwitchValue(s.section, s.label, s.oldLabel)
      if (
        !userContext.isDry &&
        currentValue !== userContext.initialValues[s.key]
      ) {
        await toggleSwitch(s.section, s.label, s.oldLabel)
      }
      return null
    },
  })),
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
